// tslint:disable:variable-name
import {HttpClient, HttpParams} from '@angular/common/http';
import {BehaviorSubject, forkJoin, Observable, of, Subscription} from 'rxjs';
import {catchError, finalize, map, tap} from 'rxjs/operators';
import { PaginatorState } from '../models/paginator.model';
import { ITableState, TableResponseModel } from '../models/table.model';
import { BaseModel } from '../models/base.model';
import { SortState } from '../models/sort.model';
import { GroupingState } from '../models/grouping.model';
import {environment} from "../models/environment.model";
import {SweetAlertService} from "../../shared/services/common/sweet-alert.service";

const DEFAULT_STATE: ITableState = {
  filter: {},
  paginator: new PaginatorState(),
  sorting: new SortState(),
  searchTerm: '',
  grouping: new GroupingState(),
  entityId: undefined
};

export abstract class TableService<T> {
  // Private fields
  private _items$ = new BehaviorSubject<T[]>([]);
  private _isLoading$ = new BehaviorSubject<boolean>(false);
  private _listTasks$ = new BehaviorSubject<any>([]);
  private _listSpecInit$ = new BehaviorSubject<any>({});
  private _listSpecAll$ = new BehaviorSubject<any>({});
  private _isFirstLoading$ = new BehaviorSubject<boolean>(true);
  private _tableState$ = new BehaviorSubject<ITableState>(DEFAULT_STATE);
  private _errorMessage = new BehaviorSubject<string>('');
  private _subscriptions: Subscription[] = [];

  // Getters
  get items$() {
    return this._items$.asObservable();
  }
  get isLoading$() {
    return this._isLoading$.asObservable();
  }
  get listTasks$() {
    return this._listTasks$.asObservable();
  }
  get listSpecInit$() {
    return this._listSpecInit$.asObservable();
  }
  get listSpecAll$() {
    return this._listSpecAll$.asObservable();
  }
  get isFirstLoading$() {
    return this._isFirstLoading$.asObservable();
  }
  get errorMessage$() {
    return this._errorMessage.asObservable();
  }
  get subscriptions() {
    return this._subscriptions;
  }
  // State getters
  get paginator() {
    return this._tableState$.value.paginator;
  }
  get filter() {
    return this._tableState$.value.filter;
  }
  get sorting() {
    return this._tableState$.value.sorting;
  }
  get searchTerm() {
    return this._tableState$.value.searchTerm;
  }
  get grouping() {
    return this._tableState$.value.grouping;
  }

  protected http: HttpClient;
  // API URL has to be overrided
  API_URL = `${environment.apiUrl}/endpoint`;
  myHeader = null;
  constructor(http: HttpClient) {
    this.http = http;
  }

  // CRUD Service OVER HERE
  create(item: any): Observable<BaseModel> {
    this._isLoading$.next(true);
    this._errorMessage.next('');
    return this.http.post<BaseModel>(this.API_URL, item, {
      headers: this.myHeader,
    }).pipe(
      finalize(() => this._isLoading$.next(false))
    );
  }

  getAll(tableState: ITableState, sortDefault = null): Observable<any> {
    const url = this.API_URL;
    this._errorMessage.next('');
    const page = tableState.paginator.page - 1;
    let params = new HttpParams()
        .set('page', page.toString())
        .set('limit', tableState.paginator.pageSize.toString())
    if (sortDefault) {
      params = params.set('sort', sortDefault);
    } else {
      params = params.set('sort', tableState.sorting.column);
    }
    // params = params.set('search', '1');
    for (const key in tableState.filter) {

      if (tableState.filter.hasOwnProperty(key)) {
        params = params.set(key, tableState.filter[key]);
      }
    }
    if (tableState.sorting.direction === 'asc') {
      params = params.set('asc', '1');
    }else {
      params = params.set('asc', '0');
    }
    return this.http.get<any>(url, {
      headers: this.myHeader,
      params
    }).pipe(
        catchError(err => {
          SweetAlertService.swalError(err)
          this._errorMessage.next(err);
          return of({ items: [], total: 0 });
        })
    );
  }

  getItemById(id: number): Observable<BaseModel> {
    this._isLoading$.next(true);
    this._errorMessage.next('');
    const url = `${this.API_URL}/${id}`;
    return this.http.get<BaseModel>(url, {
      headers: this.myHeader,
    }).pipe(
      finalize(() => this._isLoading$.next(false))
    );
  }

  update(id, item: any): Observable<any> {
    const url = `${this.API_URL}/${id}`;
    this._isLoading$.next(true);
    this._errorMessage.next('');
    return this.http.post(url, item, {
      headers: this.myHeader,
    }).pipe(
      finalize(() => this._isLoading$.next(false))
    );
  }

  delete(id: any): Observable<any> {
    this._isLoading$.next(true);
    this._errorMessage.next('');
    const url = `${this.API_URL}/${id}`;
    return this.http.delete(url, {
      headers: this.myHeader,
    }).pipe(
      finalize(() => this._isLoading$.next(false))
    );
  }

  public fetch(sortDefault = null) {
    this._isLoading$.next(true);
    this._errorMessage.next('');
    const request = this.getAll(this._tableState$.value, sortDefault)
      .pipe(
        tap((res: any) => {
          const itemsTemp = res.data.content.map((item) => {
            return item;
          });
          this._items$.next(itemsTemp);
          this.patchStateWithoutFetch({
            paginator: this._tableState$.value.paginator.recalculatePaginator(
              res.data.totalElements
            ),
          });
        }),
        catchError((err) => {
          this._errorMessage.next(err);
          return of({
            items: [],
            total: 0
          });
        }),
        finalize(() => {
          this._isLoading$.next(false);
          const itemIds = this._items$.value.map((el: T) => {
            const item = (el as unknown) as BaseModel;
            return item.id;
          });
          this.patchStateWithoutFetch({
            grouping: this._tableState$.value.grouping.clearRows(itemIds),
          });
        })
      )
      .subscribe();
    this._subscriptions.push(request);
  }

  public setDefaults() {
    this.patchStateWithoutFetch({ filter: {} });
    this.patchStateWithoutFetch({ sorting: new SortState() });
    this.patchStateWithoutFetch({ grouping: new GroupingState() });
    this.patchStateWithoutFetch({ searchTerm: '' });
    this.patchStateWithoutFetch({
      paginator: new PaginatorState()
    });
    this._isFirstLoading$.next(true);
    this._isLoading$.next(true);
    this._tableState$.next(DEFAULT_STATE);
    this._errorMessage.next('');
  }

  public patchState(patch: Partial<ITableState>) {
    this.patchStateWithoutFetch(patch);
    this.fetch();
  }

  public patchStateWithoutFetch(patch: Partial<ITableState>) {
    const newState = Object.assign(this._tableState$.value, patch);
    this._tableState$.next(newState);
  }

  // BPMN SERVICE OVER HERE
  // BPMN SERVICE OVER HERE
  apply(item: any, paramSet = [], customUrl = null): Observable<any> {
    this._isLoading$.next(true);
    this._errorMessage.next('');
    let params = new HttpParams();
    paramSet.forEach((el) => {
      params = params.set(el.name, el.value)
    })
    const url = customUrl ? customUrl : this.API_URL
    return this.http.post(url, item, {
      headers: this.myHeader,
      params
    }).pipe(
      finalize(() => this._isLoading$.next(false))
    );
  }

  getItemBpmnById(id: number, customUrl = null): Observable<BaseModel> {
    this._isLoading$.next(true);
    this._errorMessage.next('');
    const url = customUrl ? customUrl : this.API_URL + `/${id}`;
    return this.http.get<BaseModel>(url, {
      headers: this.myHeader,
    }).pipe(
      finalize(() => this._isLoading$.next(false))
    );
  }

  public fetchBpmnInit() {
    this._isLoading$.next(true);
    const stats = this.getStatistic();
    const specs = this.getSpecInit();
    forkJoin([stats, specs]).subscribe(results => {
      let stat = results[0];
      let spec = results[1];
      let arrTasks = [];
      for (const key in stat.data.tasks) {
        arrTasks.push(key)
      }
      this._listTasks$.next(arrTasks);
      this._listSpecInit$.next({
        init: spec.data
      });
      this._isLoading$.next(false);
    }, (error) => {
      SweetAlertService.swalError(error)
    });
  }

  public fetchAllSpecInit(arr) {
    this._isLoading$.next(true);
    let test = []
    arr.forEach((el) => {
      test.push(
        this.getSpecDynamic(el)
      )
    })
    forkJoin(test).subscribe((results: any) => {
      let arrTemp = new Object()
      for (let i=0; i<arr.length; i++) {
        arrTemp[arr[i]] = results[i].data
      }
      this._listSpecAll$.next(arrTemp);
      this._isLoading$.next(false);
    }, (error) => {
      SweetAlertService.swalError(error)
    });
  }

  public fetchBpmn(def = '', sortDefault = null) {
    this._isLoading$.next(true);
    this._errorMessage.next('');
    const request = this.getAllBpmn(this._tableState$.value, def, sortDefault)
      .pipe(
        tap((res: any) => {
          const itemsTemp = res.data.content.map((item) => {
            let typeTask = '';
            let nameTask = '';
            for (var key in item.tasks) {
              if (item.tasks.hasOwnProperty(key)) {
                typeTask = key
                nameTask = item.tasks[key]
              }
            }
            let obj = {
              ...item,
              typeTask: typeTask,
              nameTask: nameTask
            }
            return obj;
          });
          this._items$.next(itemsTemp);
          this.patchStateWithoutFetch({
            paginator: this._tableState$.value.paginator.recalculatePaginator(
              res.data.totalElements
            ),
          });
        }),
        catchError((err) => {
          this._errorMessage.next(err);
          return of({
            items: [],
            total: 0
          });
        }),
        finalize(() => {
          this._isLoading$.next(false);
          const itemIds = this._items$.value.map((el: T) => {
            const item = (el as unknown) as BaseModel;
            return item.id;
          });
          this.patchStateWithoutFetch({
            grouping: this._tableState$.value.grouping.clearRows(itemIds),
          });
        })
      )
      .subscribe();
    this._subscriptions.push(request);
  }

  public fetchBpmnGeneral(def = '', sortDefault = null) {
    this._isLoading$.next(true);
    this._errorMessage.next('');
    const request = this.getAllBpmn(this._tableState$.value, def, sortDefault)
      .pipe(
        tap((res: any) => {
          this._items$.next(res.data.content);
          this.patchStateWithoutFetch({
            paginator: this._tableState$.value.paginator.recalculatePaginator(
              res.data.totalElements
            ),
          });
        }),
        catchError((err) => {
          this._errorMessage.next(err);
          return of({
            items: [],
            total: 0
          });
        }),
        finalize(() => {
          this._isLoading$.next(false);
          const itemIds = this._items$.value.map((el: T) => {
            const item = (el as unknown) as BaseModel;
            return item.id;
          });
          this.patchStateWithoutFetch({
            grouping: this._tableState$.value.grouping.clearRows(itemIds),
          });
        })
      )
      .subscribe();
    this._subscriptions.push(request);
  }

  public patchStateBpmn(patch: Partial<ITableState>, def = '') {
    this.patchStateWithoutFetch(patch);
    this.fetchBpmn(def);
  }

  updateBpmn(id, item: any, type: string, paramSet = []): Observable<any> {
    const url = `${this.API_URL}/${type}/${id}/submit`;
    this._isLoading$.next(true);
    this._errorMessage.next('');
    let params = new HttpParams();
    paramSet.forEach((el) => {
      params = params.set(el.name, el.value)
    })
    return this.http.post(url, item, {
      headers: this.myHeader,
      params
    }).pipe(
      finalize(() => this._isLoading$.next(false))
    );
  }

  getAllBpmn(tableState: ITableState, def = '', sortDefault = null): Observable<any> {
    const url = this.API_URL;
    this._errorMessage.next('');
    const page = tableState.paginator.page - 1;
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', tableState.paginator.pageSize.toString())
      .set('taskDefinitionKey', def)

    // params = params.set('search', '1');
    for (const key in tableState.filter) {
      if (tableState.filter.hasOwnProperty(key)) {
        params = params.set(key, tableState.filter[key]);
      }
    }
    if (sortDefault) {
      params = params.set('sort', sortDefault);
    } else {
      params = params.set('sort', tableState.sorting.column);
    }
    if (tableState.sorting.direction === 'asc') {
      params = params.set('asc', '1');
    }else {
      params = params.set('asc', '0');
    }
    return this.http.get<any>(url, {
      headers: this.myHeader,
      params
    }).pipe(
      catchError(err => {
        // SweetAlertService.swalError(err)
        this._errorMessage.next(err);
        return of({ items: [], total: 0 });
      })
    );
  }

  getSpecInit() {
    const url = this.API_URL + '/spec';
    return this.http.get<any>(url, {
      headers: this.myHeader,
    })
  }

  getSpecDynamic(str: string) {
    const url = this.API_URL + `/${str}/spec`;
    return this.http.get<any>(url, {
      headers: this.myHeader,
    })
  }

  getStatistic() {
    const url = this.API_URL + '/statistic';
    return this.http.get<any>(url, {
      headers: this.myHeader,
    });
  }

  getSpec(type: string, task: string): Observable<any> {
    this._isLoading$.next(true);
    this._errorMessage.next('');
    const url = `${this.API_URL}/${task}/spec/${type}`;
    return this.http.get<any>(url, {
      headers: this.myHeader,
    }).pipe(
      finalize(() => this._isLoading$.next(false))
    );
  }

}
